import React from 'react';
import '../styles/designSystem.css';

/* Bascule segmentée — FORMAT STANDARD de l'application (décision 2026-08-26).
   Un libellé en petites capitales atténué, puis un groupe de boutons dans un
   cadre arrondi ; le choix actif est un aplat accent à texte blanc. Référence
   d'origine : Planification › mouvements (« Vue : Tableau | Frise »,
   « Grouper : Par personne | Par département | Par cause »).

   Toute nouvelle bascule à choix exclusif passe par ce composant. Les bascules
   antérieures gardent leur costume le temps d'être ramenées ici, une par une ;
   on n'en recode plus d'autre à la main.

     <SegmentedToggle
       label="Période"
       value={span}
       onChange={setSpan}
       options={[{ value: 1, label: '1 an' }, { value: 2, label: '2 ans' }]}
     />

   `label` est facultatif : sans lui, le groupe se suffit (bascule d'unité, par
   exemple). `options` = [{ value, label, title?, tone? }].

   `size: 'lg'` élargit la cible (2026-09-17) : une bascule de portée PAGE se
   clique plus souvent qu'un filtre de carte, et se lisait au pixel.

   `disabled` sur une OPTION (2026-09-17) : un choix sans donnée derrière lui se
   lit atténué, avec sa raison en `title`, plutôt que de disparaître — une
   commande qui s'efface laisse croire qu'elle n'existe pas, et l'écran change
   de forme d'un client à l'autre.

   `tone: 'alert'` peint le choix actif en terracotta au lieu de l'ardoise. Un
   seul usage à ce jour : « Actifs » du filtre de personnes, qui TRONQUE la
   liste — un total sous une liste filtrée ne doit jamais passer pour un total
   d'entreprise (décision 2026-08-23). Ne pas l'employer pour décorer.

   `multiple` (2026-08-27) : même costume, plusieurs choix retenus à la fois.
   `value` est alors un Set (ou un tableau) et `onChange` reçoit un Set. Aucun
   choix retenu = aucun filtre, donc TOUT s'affiche. À réserver aux listes
   courtes et stables qu'on veut voir d'un coup d'œil (les segments d'une
   offre) ; au-delà, c'est <FilterSearch> qui convient. */
export default function SegmentedToggle({
  label = null,
  value,
  onChange,
  options = [],
  className = '',
  ariaLabel = null,
  multiple = false,
  compact = false,
  size = null,
}) {
  const held = multiple
    ? (value instanceof Set ? value : new Set(value || []))
    : null;
  const isOn = (opt) => (multiple ? held.has(opt.value) : opt.value === value);
  const press = (opt) => {
    if (!multiple) { onChange(opt.value); return; }
    const next = new Set(held);
    if (next.has(opt.value)) next.delete(opt.value); else next.add(opt.value);
    onChange(next);
  };
  return (
    <div className={`lk-seg${compact ? ' lk-seg--compact' : ''}${size === 'lg' ? ' lk-seg--lg' : ''}${className ? ` ${className}` : ''}`}>
      {label && <span className="lk-seg__lbl">{label}</span>}
      <div className="lk-seg__group" role="group" aria-label={ariaLabel || label || undefined}>
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            className={`lk-seg__btn${opt.tone === 'alert' ? ' lk-seg__btn--alert' : ''}${isOn(opt) ? ' is-on' : ''}`}
            aria-pressed={isOn(opt)}
            title={opt.title || undefined}
            disabled={!!opt.disabled}
            onClick={() => !opt.disabled && press(opt)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
